import { z } from 'zod';

/**
 * The groups a member switches notifications on and off by (N17).
 *
 * **A category is a promise to a member, not a taxonomy of events.** It is the
 * unit the mobile `NotificationPreferences` screen offers a switch for, so the
 * test for a new one is "would somebody plausibly want all of this and none of
 * that" — never "which module emitted it". `matches` and `likes` are separate
 * for exactly that reason: a member who is tired of being liked still wants to
 * hear that somebody liked them back.
 *
 * **This list is the source of truth, and `notifications.defaultPreferences`
 * derives its shape from it** (`defaultPreferencesFromCatalogue`). The setting
 * used to declare nine category names as free-form record keys, which meant the
 * admin defaults grid and the delivery pipeline could disagree about what a
 * category was called and nothing would say so.
 *
 * One category still has no template keys — `system`. (`meetings` was the other
 * until P25f gave it five, and it needed no migration for them: the Postgres
 * enum already carried the value, which is the whole point of declaring a
 * category ahead of its notices.)
 * They are declared here rather than added later because the member-facing
 * switch and its stored preference row should not appear and disappear as phases
 * land, and because a category that arrives after a member has saved their
 * preferences would silently default itself back on.
 *
 * **`guardian` did arrive late (P24d2), and the warning above is why that is
 * safe here rather than in general.** Everything derives from this list — the
 * coded default via `defaultPreferencesFromCatalogue`, and
 * `NotificationPreferencesService`'s three reads, which fall through
 * `stored row → operator default → on`. So a member who saved preferences before
 * P24d2 gets the guardian switches **on**, which is the answer the platform
 * wants: these notices exist because a wali who hears nothing leaves his ward's
 * conversation frozen. A category whose correct late default was *off* would
 * have needed a migration, not a fall-through.
 *
 * **`account` (P30a) is the one category that is never a switch.** Every entry
 * under it is a `direct` challenge — a one-time code the member asked for a
 * second ago — so it bypasses preferences by construction and the grids draw
 * its row locked. Appended **last** because the Postgres enum behind this list
 * is append-only (P24d2/P25c); nothing else in that migration may name it.
 *
 * It is its own category rather than part of `matches` because **a pure wali
 * generates no `matches` traffic at all** — he is not matched with anybody. So
 * muting matches, an entirely reasonable act for a member who is also somebody's
 * guardian, would silently mute the one notice the approval gate depends on.
 */
export const NOTIFICATION_CATEGORIES = [
  'matches',
  'likes',
  'messages',
  'guardian',
  'verification',
  'billing',
  'courses',
  'meetings',
  'moderation',
  'legal',
  'system',
  'announcements',
  'account',
] as const;

export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

const CATEGORY_SET: ReadonlySet<string> = new Set(NOTIFICATION_CATEGORIES);

/** Narrows an arbitrary string — a stored preference row's column, say. */
export function isNotificationCategory(value: string): value is NotificationCategory {
  return CATEGORY_SET.has(value);
}
